(ns frontend.ux-overhaul.nev.components.activity-timeline
  (:require [basic-components.field-editor :as field-editor]
            [clojure.set :as set]
            [clojure.string :as string]
            [common.date-formats :as date-formats]
            [common.dates :as dates]
            [common.entity :as entity]
            [common.features.plan :as plan]
            [common.localization :as loc]
            [common.schema.registry :as registry]
            [common.timezones :as timezones]
            [common.util :as cu]
            [common.value-creation-status :as value-creation-status]
            [frontend.account :as account]
            [frontend.actions.attachment :as attachment-actions]
            [frontend.actions.email :as actions-email]
            [frontend.actions.entity :as entity-actions]
            [frontend.bootstrap.components :as rbs]
            [frontend.logging :as logging]
            [frontend.routing :as routing]
            [frontend.state.current-session :as session]
            [frontend.tenant-customizations :as tenant-customizations]
            [frontend.utils :as fu]
            [frontend.view.entity-viewers.activity.events :as events]
            [frontend.view.entity-viewers.activity.history-activity :as history-activity]
            [frontend.view.modals.activity-entity-editor.view :as activity-entity-editor]
            [frontend.view.modals.email-editor.view :as email-editor-modal]
            [frontend.view.modals.email-viewer.events :as email-events]
            [frontend.view.modals.email-viewer.view :as email-viewer-modal]
            [frontend.view.modals.events :as modals]
            [frontend.view.modals.textarea.view :as textarea-modal]
            [frontend.view.modals.value-creation.view :as value-creation]
            [frontend.zap-time :as zap-time]
            [oops.core :as oops]
            [reagent.core :as r]
            [ui.components.button.view :as button]
            [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
            [ui.components.rich-text.tiptap :as tiptap]
            [ui.dom-globals :as dg]))

;; Components

(def content-attributes
  {:entity.type/note            [:note/message]
   :entity.type/note-message    [:note-message/message]
   :entity.type/note-call       [:note-call/message]
   :entity.type/note-meeting    [:note-meeting/message]
   :entity.type/email           [:email/snippet :email/body]
   :entity.type/system-activity nil                                                                 ;; no-content
   })

(def expanded-content-attributes
  {:entity.type/note            [:note/message]
   :entity.type/note-message    [:note-message/message]
   :entity.type/note-call       [:note-call/message]
   :entity.type/note-meeting    [:note-meeting/message]
   :entity.type/email           [:email/body]
   :entity.type/system-activity nil                                                                 ;; no-content
   })


(defn parse-email-date
  [str-date]
  ;; `when`, not `when-not`, and `non-empty-string?`, not `empty-string`.
  ;; cu/empty-string is (or s ""), which is truthy for every input including
  ;; nil, so the old guard made this whole function return nil always and the
  ;; parse below had never once run. action-ts therefore fell through to
  ;; :common/created-on for any email carrying a string :email/date, i.e. it
  ;; ordered them by when the row was created rather than when the mail was
  ;; sent. See ai/mistakes.md.
  (when (cu/non-empty-string? str-date)
    ;;rfc-2822-date-format e.g. "Mon, 15 Apr 2019 09:07:50 GMT"
    (let [pattern "EEE, dd MMM yyyy HH:mm:ss Z"
          dt-val (dates/date str-date {:pattern pattern})
          ms-val (dg/date->millis dt-val)]
      ;; dates/date hands back its seed object when the string does not match
      ;; the pattern - goog's DateTimeParse reports the failure in a return
      ;; value that dates/date discards - and that seed is year 0, a large
      ;; negative millis that would sort the entry above everything else.
      ;; Returning nil instead keeps action-ts on the :common/created-on
      ;; fallback it has been using all along for these.
      (when (and ms-val (pos? ms-val))
        ms-val))))

(defn action-ts
  [entity]
  (let [explicit (case (:entity/type entity)
                   :entity.type/note-call (:note-call/timestamp entity)
                   :entity.type/note-meeting (:note-meeting/timestamp entity)
                   :entity.type/note-message (:note-message/timestamp entity)
                   :entity.type/email (cond (number? (:email/date entity))
                                            (:email/date entity)
                                            (string? (:email/date entity))
                                            (parse-email-date (:email/date entity))
                                            :else
                                            nil)
                   nil)]
    (or explicit (:common/created-on entity))))

(defn action-tz?
  [entity]
  (case (:entity/type entity)
    :entity.type/note-call false
    :entity.type/note-meeting false
    :entity.type/note-message false
    :entity.type/email (cond (number? (:email/date entity))
                             false
                             (string? (:email/date entity))
                             true
                             :else
                             true)
    true))

(defn pass-or-on-hold-meta
  [text entity]
  (let [comment-text (string/trim (str (:system-activity/comment
                                        entity)))]
    [:span text
     (when (or (not (session/is-read-only-user?))
               (seq comment-text))

       [button/square-secondary-open
        {:tooltip    comment-text
         :class-name (fu/cs :system-activity-comment-icon
                            :fal
                            (if-not (empty? comment-text)
                              :fa-comment
                              :fa-comment-plus))
         :on-click   (fn [_]
                       (when-not (or (session/is-guest?)
                                     (session/is-read-only-user?))
                         (modals/open-modal!
                          textarea-modal/modal-key
                          {:close-modal! identity
                           :title-text   (loc/tr :edit)
                           :button-text  (loc/tr :ok)
                           :value        (:system-activity/comment
                                          entity)
                           :on-submit!   (fn [updated-text]
                                           (entity-actions/*->field-update!
                                            (:db/id entity)
                                            :system-activity/comment
                                            updated-text
                                            (fn [_]
                                              #_(logging/warn "Success"))
                                            (fn [_]
                                              (logging/error "Failed to update activity comment"))))})))}])]))

(defn meta-text
  [entity-type entity]
  (let [creator-name (account/actor-display-name (:common/created-by entity))
        merge-info (some-> entity
                           :system-activity/info)
        merge-entity-type-name-lc (some-> merge-info
                                          :entity-type
                                          loc/tr
                                          string/lower-case)
        merge-source (some-> entity
                             :system-activity/info
                             :source-display-string)
        caller-name (account/actor-display-name (:note-call/call-made-by entity))
        email-sender (string/lower-case (str (:email/from entity)))
        email-actor-name
        (if (= email-sender (string/lower-case (str (:account/email @session/account))))
          (loc/tr :you)
          (or (->> (concat @session/all-accounts @session/all-persons)
                   (keep (fn [a-or-p]
                           (when (= email-sender
                                    (string/trim
                                     (str (or (:account/email a-or-p)
                                              (:person/universal-email-address a-or-p)))))
                             (entity/display-string a-or-p))))
                   (first))
              email-sender))]
    (case entity-type
      :entity.type/note (loc/tr :activity-label-note
                          [creator-name])
      :entity.type/value-creation (loc/tr :activity-label-value-creation-activity
                                    [creator-name])
      :entity.type/email (loc/tr :activity-label-email [email-actor-name])
      :entity.type/note-call (if (= (:note-call/type entity) :inbound)
                               (loc/tr :activity-label-note-call-inbound [creator-name])
                               (loc/tr :activity-label-note-call-outbound
                                 [(or caller-name creator-name)]))
      :entity.type/note-meeting (loc/tr :activity-label-note-meeting
                                  [creator-name])
      :entity.type/system-activity
      (case (:system-activity/type entity)
        :system-activity.type/pass [pass-or-on-hold-meta
                                    (loc/tr :activity-label-system-activity-pass
                                      [(->> (:system-activity/pass-reason entity)
                                            (get @session/entities)
                                            (entity/display-string))
                                       creator-name])
                                    entity]
        :system-activity.type/put-on-hold [pass-or-on-hold-meta
                                           (loc/tr :activity-label-system-activity-put-on-hold
                                             [(fu/display-date-timestamp
                                               (:system-activity/on-hold-until entity))
                                              creator-name])
                                           entity]
        :system-activity.type/reactivate (loc/tr :activity-label-system-activity-reactivate
                                           [creator-name])
        :system-activity.type/create (loc/tr :activity-label-system-activity-create
                                       [creator-name])
        :system-activity.type/loss [pass-or-on-hold-meta
                                    (loc/tr :activity-label-system-activity-loss
                                      [creator-name])
                                    entity]
        :system-activity.type/won
        (let [related-entities (->> entity
                                    :common/related-entities
                                    (map (fn [id]
                                           (get @session/entities id))))
              fund-commitment (or (->> related-entities
                                       (cu/only-entity :entity.type/fund-commitment)
                                       first
                                       (get @session/entities))
                                  (->> related-entities
                                       (cu/only-entity :entity.type/fundraising-opportunity)
                                       first
                                       :fundraising-opportunity/fund-commitment
                                       (get @session/entities)))
              won-by (->> fund-commitment
                          :fund-commitment/won-by
                          (get @session/entities))
              won-by-name (entity/display-string won-by)]
          (loc/tr :activity-label-system-activity-won
            [(or won-by-name creator-name)]))
        :system-activity.type/stage-change (loc/tr :activity-label-system-activity-stage-change
                                             [(->> (:system-activity/target-stage entity)
                                                   (get @session/entities)
                                                   (entity/display-string))
                                              creator-name])
        :system-activity.type/entity-merge (loc/tr :activity-label-system-activity-entity-merge
                                             [merge-source
                                              merge-entity-type-name-lc])
        :system-activity.type/oppo-move nil                                                         ;; do not show in activity timeline
        (logging/warn "No activity type for " (:db/id entity)))
      :entity.type/note-message (loc/tr :activity-label-note-message
                                  [creator-name
                                   (case (:note-message/message-type entity)
                                     :message.type/other ""                                         ;; do not show type for other
                                     (loc/tr (:note-message/message-type entity)))])
      (str "Activity meta text missing : " entity-type))))


(defn action-item
  [action-id _event-key icon label on-select!]
  [:div.ml-2
   {:on-click on-select!}
   [fu/tooltip-overlay
    {:id        (str "action-" action-id)
     :placement "top"}
    label
    [:i {:class-name (fu/cs :fal icon)}]]])

(defn activity-actions
  [local-entities nev-entity-id entity]
  (let [action-id (:db/id entity)
        create-menu-item (partial action-item action-id)
        menu-actions
        {:open      (create-menu-item :open
                                      :fa-eye
                                      (loc/tr :view)
                                      #(modals/open-modal!
                                        email-viewer-modal/modal-key
                                        {:email-id         (:db/id entity)
                                         :local-entities   local-entities
                                         :viewed-entity-id nev-entity-id}))
         :forward   (create-menu-item :forward
                                      :fa-forward
                                      (loc/tr :forward)
                                      #(email-events/forward nev-entity-id entity))
         :reply     (create-menu-item :reply
                                      :fa-reply
                                      (loc/tr :reply)
                                      #(email-events/reply nev-entity-id entity))
         :reply-all (create-menu-item :reply-all
                                      :fa-reply-all
                                      (loc/tr :reply-all)
                                      #(email-events/reply nev-entity-id entity))
         :delete    (create-menu-item :delete
                                      :fa-trash
                                      (loc/tr :delete)
                                      #(events/dropdown-remove-selected local-entities
                                                                        action-id
                                                                        nev-entity-id
                                                                        identity
                                                                        identity))
         ;; Not implemented:
         #_#_:pin
                 (create-menu-item :pin
                                   :fa-map-marker-alt
                                   (loc/tr :pin)
                                   #(events/pin-selected action-id nev-entity-id identity identity))
         :edit      (create-menu-item :edit
                                      :fa-edit
                                      (loc/tr :edit)
                                      (fn [_]
                                        (if (= :entity.type/value-creation (:entity/type entity))
                                          (modals/open-modal! value-creation/modal-key
                                                              {:containing-entity-id nev-entity-id
                                                               :entity-id            action-id})
                                          (modals/open-modal! activity-entity-editor/modal-key
                                                              {:entity-id        action-id
                                                               :viewed-entity-id nev-entity-id
                                                               :primary-button-text
                                                               (when (= :entity.type/email
                                                                        (:entity/type entity))
                                                                 (loc/tr :submit))}))))}
        related-actions
        (if-not (= (:entity/type entity) :entity.type/email)
          ;; Note: if :edit is ever used for email, local-entities must be passed to activity-entity-editor:
          [:edit :delete :pin]
          (if (and (plan/available :feature/nylas-integration @session/tenant @session/role)
                   (:account/nylas-account-id @session/account))
            [:open :forward :reply :reply-all :delete :pin]
            [:open :delete :pin]))]
    [:div.d-flex.align-items-center                                                                 ;.activity-actions-container
     (doall (->> related-actions
                 (select-keys menu-actions)
                 (map (fn [[k menu-item]]
                        ^{:key (str "activity-event-dropdown-manu-" action-id "-item-" (pr-str k))}
                        menu-item))))]))

(defn attachments-dropdown
  [nev-entity-id _entity attachments]
  (let [nev-entity (get @session/entities nev-entity-id)
        entity-attachments (:common/attachments nev-entity)]
    [dropdown-menu/view
     {:trigger [dropdown-menu/button-trigger-open
                [:i.fas.fa-paperclip]]
      :items   (->> attachments
                    (mapv (fn [attachment]
                            (let [attachment-id (:db/id attachment)]
                              {:value
                                             ^{:key (str "attachment-" attachment-id)}
                                             [:div
                                              {:on-click   (fn [e]
                                                             (let [target (dg/target e)
                                                                   tag (oops/oget target "tagName")]
                                                               (when-not (= tag "I")
                                                                 (fu/open-attachment! attachment-id))))
                                               :class-name ""}
                                              [:span (:attachment/filename attachment)]
                                              [:span
                                               {:class "flex justify-between items-center gap-2"}
                                               [button/square-secondary-open
                                                {:on-click #(fu/open-attachment! attachment-id)
                                                 :tooltip  (loc/tr :open)}
                                                [:i.fal.fa-external-link]]
                                               [button/square-secondary-open
                                                {:on-click #(fu/download-attachment! attachment-id)
                                                 :tooltip  (loc/tr :download)}
                                                [:i
                                                 {:class (fu/cs :fal
                                                                :fa-cloud-download
                                                                (when-not (some #{attachment-id} entity-attachments)
                                                                  :mr-1))}]]
                                               (when-not (or (session/is-read-only-user?)
                                                             (some #{attachment-id} (:common/attachments nev-entity)))
                                                 [button/square-secondary-open
                                                  {:tooltip (loc/tr :copy-to-specific-entity-attachments
                                                              [(cu/str-trim-lcase (loc/tr (:entity/type nev-entity)))])}
                                                  [:i.fal.fa-file-upload
                                                   {:on-click (fn [_e]
                                                                (attachment-actions/*->copy-to attachment-id
                                                                                               nev-entity-id
                                                                                               (fn [_])
                                                                                               (fn [_])))}]])
                                               (when (and (plan/available :feature/nylas-integration @session/tenant @session/role)
                                                          (:account/nylas-account-id @session/account))
                                                 [button/square-secondary-open
                                                  {:on-click (fn [_e]
                                                               (modals/open-modal!
                                                                email-editor-modal/modal-key
                                                                {:title-text         (loc/tr :send-file)
                                                                 :entity-id          nev-entity
                                                                 :attachments        #{attachment-id}
                                                                 :on-primary-submit! (fn [email success! error!]
                                                                                       (actions-email/*->send-email
                                                                                        email
                                                                                        success!
                                                                                        error!))}))}
                                                  [:i.fal.fa-share]])]]
                               :custom-item? true})))
                    (doall))}]))

(defn activity-elem
  [{:keys [state local-entities viewed-entity-id entity class-name]}]
  (let [last-updated-by (:common/updated-by entity)
        last-updated-on (:common/updated-on entity)
        df (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
        tz (or (:tenant/timezone @session/tenant) timezones/default-timezone)
        created-on (:common/created-on entity)
        entity-type (:entity/type entity)
        title-text (meta-text entity-type entity)
        privacy-status (when (and (= entity-type :entity.type/email)
                                  (= (:email/owner entity) (:db/id @session/account)))
                         (or (:email/privacy-status entity)
                             :privacy-status/public))
        activity-entity-id (:db/id entity)

        override-expand-entity (:override-expand-entity @state)

        expanded? (if (contains? override-expand-entity activity-entity-id)
                    (get override-expand-entity activity-entity-id)
                    (:expand-all? @state))

        content-attributes (if expanded?
                             (get expanded-content-attributes entity-type)
                             (get content-attributes entity-type))
        meeting? (= entity-type :entity.type/note-meeting)
        participants (when meeting?
                       (->> entity
                            :note-meeting/participants
                            (keep session/get-entity)))
        attachment-ids (:common/attachments entity)
        attachments (->> attachment-ids
                         (map (fn [aid]
                                (or (get @local-entities aid)
                                    (session/get-entity aid))))
                         (doall))
        other-related-entities
        (->>
         (set (:common/related-entities entity))
         (keep
          (fn [id]
            (let [r-e (get @session/entities id)]
              (when
               (and (not= id viewed-entity-id)
                    (some #{(:entity/type r-e)}
                          [:entity.type/person
                           :entity.type/company
                           :entity.type/investor
                           :entity.type/oppo
                           :entity.type/holding
                           :entity.type/sale-agreement
                           :entity.type/fundraising-opportunity
                           :entity.type/sales-opportunity
                           :entity.type/fund-commitment
                           :entity.type/fund
                           :entity.type/real-estate
                           :entity.type/fof-asset
                           :entity.type/other-asset]))
                r-e))))
         (remove
          (fn [e]
            (let [entity-type (:entity/type e)
                  no-fundraising? (not (session/fundraising?))
                  no-portfolio? (not (session/portfolio?))
                  no-fund-management? (not (session/has-feature-access? :feature/fund-management))
                  no-real-estate? (not (session/has-feature-access?
                                        :feature/asset-types-real-estate))
                  no-company? (not (session/has-feature-access? :feature/asset-types-company))
                  no-fof-asset? (not (session/has-feature-access? :feature/asset-types-fof))
                  no-other-asset? (not (session/has-feature-access? :feature/asset-types-other))
                  no-sales-pipelines? (not (session/sales-pipelines?))
                  no-exit-process-management? (not (session/exit-process-management?))
                  no-dealflow? (not (session/dealflow?))]
              (case entity-type
                :entity.type/fund-commitment
                no-fundraising?
                :entity.type/fund
                (or no-fundraising?
                    no-fund-management?)
                :entity.type/fundraising-opportunity
                no-fundraising?
                :entity.type/sales-opportunity
                no-sales-pipelines?
                :entity.type/sale-agreement
                no-sales-pipelines?
                :entity.type/exit-opportunity
                no-exit-process-management?
                :entity.type/holding
                no-portfolio?
                :entity.type/investor
                (and no-portfolio? no-fundraising?)
                :entity.type/real-estate
                no-real-estate?
                :entity.type/company
                no-company?
                :entity.type/fof-asset
                no-fof-asset?
                :entity.type/other-asset
                no-other-asset?
                :entity.type/oppo
                no-dealflow?
                false)))))
        tags (:activity/tag-tags entity)
        on-card-click
        (fn [e]
          (let [target (dg/target e)
                classes (oops/oget target "classList")
                values (string/split (oops/oget classes "value") #" ")
                is-dropdown-option? (contains? (set values) "dropdown-item")]
            (when-not (or is-dropdown-option?
                          (get-in @state [:prevent-click? activity-entity-id]))
              (swap! state assoc-in [:override-expand-entity activity-entity-id] (not expanded?)))
            (dg/prevent-and-stop e)))
        on-card-mouse-down
        (fn [e]
          (let [target (dg/target e)
                classes (oops/oget target "classList")
                values (string/split (oops/oget classes "value") #" ")
                is-toggle (contains? (set values) "toggle-dropdown-el")]
            (when-not is-toggle
              (swap! state assoc-in [:prevent-click? activity-entity-id] false)
              (js/setTimeout (fn []
                               (swap! state assoc-in [:prevent-click? activity-entity-id] true))
                             200))))
        content-height (get-in @state [:content-height (:db/id entity)])
        ;; Must match the collapsed max-height below.
        content-height-limit 75
        has-expandable-content? (< content-height-limit content-height)]
    (when title-text
      [:li
       {:class-name (fu/cs "activity-entry" "new-nev-activity-entry" class-name)
        :key        (str "activity-entry-" (:db/id entity))}
       [:div.activity-entry-left
        [:span.activity-bubble
         [:i {:class-name (fu/cs :fal (entity/default-icon entity))}]]]
       [:div.activity-entry-right
        [:div
         {:class-name    (fu/cs (if (or (and (pos? (count other-related-entities))
                                             (not (session/is-guest?)))
                                        (and (coll? content-attributes)
                                             (seq content-attributes)))
                                  :activity-entry-block
                                  :activity-entry-system-block))
          :on-click      on-card-click
          :on-mouse-down on-card-mouse-down
          :style         {:cursor "pointer !important"}}
         [:div
          {:class "flex items-center justify-between gap-2 cursor-pointer text-xs text-base-light-text-paler"}
          [:div
           {:class "flex gap-2"}
           [:span
            (date-formats/format-millis (action-ts entity)
                                        df
                                        (when (action-tz? entity)
                                          tz))]
           [:span
            " | "]
           (when privacy-status
             [:div
              {:class "*:p-0"}
              [button/secondary-open
               {:tooltip  (if (= privacy-status :privacy-status/private)
                            (loc/tr :privacy-status/private-tooltip)
                            (loc/tr :privacy-status/public-tooltip))
                :on-click (fn [_]
                            (entity-actions/*->field-update! (:db/id entity)
                                                             :email/privacy-status
                                                             (if (= privacy-status
                                                                    :privacy-status/private)
                                                               :privacy-status/public
                                                               :privacy-status/private)
                                                             (fn [_]
                                                               #_(logging/warn "Success"))
                                                             (fn [_]
                                                               (logging/error
                                                                "Failed to update email privacy"))))}]])
           [:span title-text]
           (when (and last-updated-on
                      last-updated-by
                      ;; hack to hide last updated by in data enrichment
                      (> (- last-updated-on created-on) 2000))
             [:span
              (loc/tr :activity-entry-updated-text
                [(account/actor-display-name last-updated-by :middle-of-sentence? true)
                 (date-formats/format-millis last-updated-on
                                             df
                                             tz)])])]
          [:div
           {:class "flex items-center gap-2"}
           (when has-expandable-content?
             (let [tooltip-copy (if expanded?
                                  (loc/tr :compress)
                                  (loc/tr :expand))]
               [button/secondary-open
                {:tooltip tooltip-copy}
                (if expanded?
                  [:i.fal.fa-compress-alt]
                  [:i.fal.fa-expand-alt])]))

           (when-not (or (session/is-read-only-user?)
                         (= (:entity/type entity) :entity.type/system-activity))
             [activity-actions local-entities viewed-entity-id entity])]]
         (when-not (empty? tags)
           [:div
            (->> tags
                 (map (fn [tag]
                        (let [tag-entity (get @session/entities tag)
                              title (:named-value/value tag-entity)]
                          [:span
                           {:class-name (fu/cs "zap-badge zap-badge--secondary mr-1 mt-2")
                            :key        (str "note-tag-" tag)}
                           title])))
                 (doall))])
         (when (and (coll? content-attributes)
                    (seq content-attributes))
           [:div
            {:class-name (fu/cs "pt-2 text-xs relative"
                                (when (and (not expanded?) has-expandable-content?)
                                  "max-h-[75px] overflow-y-hidden before:content-[''] before:absolute before:bottom-0 before:left-0 before:right-0 before:h-8 before:z-10 before:pointer-events-none before:bg-gradient-to-b before:from-transparent before:to-white"))}
            [:div
             (let [content (->> content-attributes
                                (map (fn [content-attribute]
                                       (get entity content-attribute)))
                                (remove cu/blank-value?)
                                (first)
                                (str)
                                (string/trim))]
               (when (not-empty content)
                 ^{:key (str "activity-entry-content" (:db/id entity) "-" expanded?)}
                 [:div
                  {:ref (fn [el]
                          (when el
                            (let [height (oops/oget el "offsetHeight")
                                  prev-height (get-in @state [:content-height (:db/id entity)])]
                              (when-not (= height prev-height)
                                (swap! state assoc-in [:content-height (:db/id entity)] height)))))}
                  [tiptap/viewer {:value content}]]))]])
         [:div.activity-entry-footer
          (when (seq attachments)
            [attachments-dropdown viewed-entity-id entity attachments])
          (when (and (pos? (count other-related-entities))
                     (not (session/is-guest?)))
            [:div.activity-entry-footer__related-entities
             [:span.activity-footer-text
              (loc/tr :associated-also-with)]
             [:ul.associated-with-list
              (doall
               (for [rel other-related-entities]
                 [:li.associated-with-element
                  {:on-click (fn [e]
                               (routing/open-entity (:db/id rel))
                               (dg/prevent-and-stop e))
                   :key      (str "associated-with" (:db/id rel))}
                  [:i {:class-name (fu/cs :fal (entity/default-icon rel))}]
                  [:span.associated-with-text
                   (entity/resolve-entity-name rel session/get-entity session/reverse-lookup)]
                  (when-not (session/is-read-only-user?)
                    [:i.fal.fa-times
                     {:on-click (fn [e]
                                  (events/remove-action-relationship
                                   local-entities
                                   (:db/id entity)
                                   (:db/id rel)
                                   #(logging/log "SAVED OK")
                                   #(logging/error "Failed to remove related entity : " %))
                                  (dg/prevent-and-stop e))}])]))]])]
         (when (and meeting?
                    (seq participants))
           [:div.activity-entry-footer
            [:div.activity-entry-footer__participants
             [:div
              [:span.activity-footer-text.meeting-participants (loc/tr :our-participants)]
              [:span.meeting-participants__list
               (doall
                (->> participants
                     (map entity/display-string)
                     (string/join ", ")))]]]])]]])))

(defn value-creation-elem
  [{:keys [entity class-name state local-entities viewed-entity-id]}]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        df (or (:tenant/date-format tenant) (loc/tr :date-format))
        tz (or (:tenant/timezone tenant) timezones/default-timezone)
        last-updated-by (:common/updated-by entity)
        last-updated-on (:common/updated-on entity)
        created-on (:common/created-on entity)
        entity-type (:entity/type entity)
        header-title-text (meta-text entity-type entity)
        title (:value-creation/title entity)

        value-creation-entity-id (:db/id entity)

        override-expand-entity (:override-expand-entity @state)

        expanded? (if (contains? override-expand-entity value-creation-entity-id)
                    (get override-expand-entity value-creation-entity-id)
                    (:expand-all? @state))

        categories-label (if (:value-creation/holding-category entity)
                           (loc/tr :value-creation-category
                             [(->> (session/get-entity (:value-creation/holding-category entity))
                                   :named-value/value)])
                           (loc/tr :value-creation-categories
                             [(->> (:value-creation/investor-categories entity)
                                   (map (fn [category-id]
                                          (:named-value/value (session/get-entity category-id))))
                                   (string/join ", "))]))
        attachment-ids (:common/attachments entity)
        attachments (->> attachment-ids
                         (map (fn [aid]
                                (or (get @local-entities aid)
                                    (session/get-entity aid))))
                         (doall))
        status (:value-creation/status entity)
        status-label (value-creation-status/get-label status)
        managers-label (if (:value-creation/our-managers entity)
                         (loc/tr :value-creation-managers
                           [(->> (:value-creation/our-managers entity)
                                 (map (fn [manager-id]
                                        (fu/display-entity manager-id)))
                                 (string/join ", "))])
                         (loc/tr :value-creation-manager
                           [(fu/display-entity (:value-creation/our-manager entity))]))
        work-in-progress? (some #{status}
                                [:value-creation.status/work-in-progress
                                 :value-creation.status/almost-finished])
        done? (= status :value-creation.status/done)
        description-and-lessons (:value-creation/description-and-lessons-learned entity)
        on-card-click (fn [e]
                        (let [target (dg/target e)
                              classes (oops/oget target "classList")
                              values (string/split (oops/oget classes "value") #" ")
                              is-dropdown-option? (contains? (set values) "dropdown-item")]
                          (when-not (or is-dropdown-option?
                                        (get-in @state [:prevent-click? value-creation-entity-id]))
                            (swap! state assoc-in
                                   [:override-expand-entity value-creation-entity-id]
                                   (not expanded?)))
                          (dg/prevent-and-stop e)))
        on-card-mouse-down
        (fn [e]
          (let [target (dg/target e)
                classes (oops/oget target "classList")
                values (string/split (oops/oget classes "value") #" ")
                is-toggle (contains? (set values) "toggle-dropdown-el")]
            (when-not is-toggle
              (swap! state assoc-in [:prevent-click? value-creation-entity-id] false)
              (js/setTimeout
               (fn []
                 (swap! state assoc-in [:prevent-click? value-creation-entity-id] true))
               200))))]
    (when title
      [:li
       {:class-name (fu/cs "activity-entry" class-name)
        :key        (str "activity-entry-" (:db/id entity))}
       [:div
        {:class-name (fu/cs "activity-entry-left"
                            (when work-in-progress?
                              "activity-entry-left__in-progress")
                            (when done?
                              "activity-entry-left__done"))}
        [:span.activity-bubble
         [:i {:class-name (fu/cs "fal fa-dollar-sign")}]]]
       [:div.activity-entry-right
        [:div
         {:class-name    (fu/cs :activity-entry-block)
          :on-mouse-down on-card-mouse-down
          :on-click      on-card-click}
         [:div.activity-entry-header.cursor-pointer
          [:span.activity-entry-header__date
           (date-formats/format-millis (action-ts entity)
                                       df
                                       (when (action-tz? entity)
                                         tz))]
          [:span.activity-entry-header__text-divider
           " | "]
          [:span.activity-entry-header__text header-title-text]
          (when (and last-updated-on
                     last-updated-by
                     (not= last-updated-on created-on))
            [:span.activty-entry-header__text
             (loc/tr :activity-entry-updated-text
               [(account/actor-display-name last-updated-by :middle-of-sentence? true)
                (date-formats/format-millis last-updated-on
                                            df
                                            tz)])])
          [:div
           {:class "flex items-center gap-2"}
           (let [tooltip-copy (if expanded?
                                (loc/tr :compress)
                                (loc/tr :expand))]
             [button/secondary-open
              {:tooltip tooltip-copy}
              (if expanded?
                [:i.fal.fa-compress-alt.cursor-pointer]
                [:i.fal.fa-expand-alt.cursor-pointer])])
           (when-not (session/is-read-only-user?)
             [activity-actions local-entities viewed-entity-id entity])]]
         [:div
          {:class-name (fu/cs "activity-entry-content value-creation-content")}
          [:div
           [:h2.title-h4 title]
           [:div.mt-1
            [:div
             {:class-name (fu/cs (when-not expanded?
                                   "activity-truncated-text"))}
             description-and-lessons]
            (when-not expanded?
              [:span.show-all])]]

          (if expanded?
            [:div.mt-4
             (->> (registry/get-schema tenant-schema :entity.type/value-creation)
                  (remove (fn [[k _]]
                            (or (some #{(namespace k)} ["common" "db"])
                                (some #{k}
                                      [:value-creation/title
                                       :value-creation/description-and-lessons-learned]))))
                  (keep (fn [[k fs]]
                          (let [display-str (fu/display-data tenant-schema tenant k fs entity)]
                            (when-not (cu/blank-value? display-str)
                              (str (loc/field-name k fs) ": " display-str)))))
                  (map-indexed (fn [idx txt]
                                 ^{:key (str "value-creation-attr-" txt)}
                                 [:div
                                  {:class (fu/cs "text-size--sm"
                                                 (when (pos? idx) "mt-1"))}
                                  txt]))
                  (doall))]
            [:div.mt-1
             [:div.text-size--sm categories-label]
             [:div.text-size--sm.mt-1 (loc/tr :value-creation-status [status-label])]
             [:div.text-size--sm.mt-1 managers-label]])
          [:div.activity-entry-footer
           (when (seq attachments)
             [attachments-dropdown viewed-entity-id entity attachments])]]]]])))

(defn resolve-person-emails
  [local-entities entity-id]
  (->> @local-entities
       (vals)
       (cu/filter-by :entity/type :entity.type/email)
       (filter (fn [email]
                 (some #{entity-id} (:common/related-entities email))))
       (remove (fn [e]
                 (and (= (:email/privacy-status e) :privacy-status/private)
                      (not= (:email/owner e) (:db/id @session/account)))))))

(defn resolve-associated-people-email-ids-for-entity
  [local-entities entity-id]
  (let [associations (fu/related-entity-associations :entity.type/person entity-id)
        related-person-and-email-ids
        (->> associations
             (mapcat (fn [association]
                       (let [person (fu/get-association-party association :entity.type/person)
                             person-emails (resolve-person-emails local-entities (:db/id person))]
                         (->> person-emails
                              (map (fn [pe]
                                     [(:db/id pe) (:db/id person)])))))))]
    related-person-and-email-ids))

(defn filter-by-tags-dropdown
  [options local-state on-change!]
  [:div.mt-2
   [:div.select-area.select-area--white.mt-2
    [field-editor/view
     {:editor/type  :editor.type/multi-select
      :value        (:selected-tags @local-state)
      :state-atom   local-state
      :placeholder  (loc/tr :filter-activity-by-tag)
      :value-type   :number
      :attribute    :selected-tags
      :label        ""
      :hide-label?  true
      :on-save!     (fn [_e new-value clear-cb!]
                      (on-change! new-value)
                      (when clear-cb!
                        (clear-cb!)))
      :drag-options {:hide-drag-area? true}
      :options      options}]]])

(defn no-results-found
  []
  [:div.no-results__container.mt-4
   [:img {:src "/img/undraw_no_data_qbuo.svg"}]
   [:div (loc/tr :no-results-found)]])

(defn resolve-activity-entities
  [activity-entity-types reverse-relationship-datas-by-entity-type]
  (->> activity-entity-types
       (mapcat #(get reverse-relationship-datas-by-entity-type %))
       (group-by (fn [[_ _ entity]]
                   (:db/id entity)))
       (map (fn [[_eid entity-datas]]
              (first entity-datas)))
       (sort-by (fn [[_ _ entity]]
                  (or (:note-call/timestamp entity)
                      (:note-meeting/timestamp entity)
                      (:note-message/timestamp entity)
                      (:email/date-ts entity)
                      ;;can be removed when date-ts migration has been run:
                      (when-let [dt (:email/date entity)]
                        (let [parse-date (dates/date dt {:pattern date-formats/rfc-2822})
                              js-date (dates/to-native parse-date)]
                          (dg/date->millis js-date)))
                      (:common/created-on entity)
                      (:common/updated-on entity)
                      0)))
       (reverse)))

(defn entity-activity-view
  [primary-entity-id state show-more! local-entities reverse-relationship-datas-by-entity-type
   activity-entity-types]
  (let [tenant-schema @session/tenant-schema
        resolved-entities @(r/track resolve-activity-entities
                                    activity-entity-types
                                    reverse-relationship-datas-by-entity-type)
        filtered-items (if (empty? (:selected-tags @state))
                         resolved-entities
                         (->> resolved-entities
                              (filter (fn [[_ _ entity]]
                                        (some (:selected-tags @state)
                                              (:activity/tag-tags entity))))))
        removed-entity-ids (some->> @local-entities
                                    (vals)
                                    (filter :common/disabled?)
                                    (map :db/id))
        filtered-items (some->> filtered-items
                                (remove (fn [i]
                                          (some #{(:db/id i)} (set removed-entity-ids)))))
        item-count (count filtered-items)
        show-count (:show-count @state)
        show-all? (or (< item-count 11)
                      (and show-count
                           (>= show-count item-count)))
        show-count (or show-count 5)
        visible-items (if show-all?
                        filtered-items
                        (take show-count
                              filtered-items))
        last-data (last visible-items)
        activity-tag-options (fu/named-value-options (registry/get-field-schema tenant-schema
                                                                                :activity/tag-tags)
                                                     nil)
        set-tag-filter! (fn [new-value]
                          (swap! state assoc :selected-tags new-value))
        activities
        (->> visible-items
             (remove (fn [[entity-type _ entity]]
                       (and (= entity-type :entity.type/value-creation)
                            (:value-creation/status entity)
                            (not= :value-creation.status/done (:value-creation/status entity))))))
        value-creations (->> visible-items
                             (map (fn [[_ _ entity]]
                                    entity))
                             (cu/only-entity :entity.type/value-creation)
                             (remove (fn [vce]
                                       (= :value-creation.status/done
                                          (:value-creation/status vce)))))]
    [:div
     [filter-by-tags-dropdown activity-tag-options state set-tag-filter!]
     (if (empty? filtered-items)
       [no-results-found]
       [:ul.activity-entities
        (doall
         (for [entity value-creations]
           ^{:key (str "value-creation-" (:db/id entity))}
           [value-creation-elem
            {:state            state
             :local-entities   local-entities
             :viewed-entity-id primary-entity-id
             :entity           entity
             :class-name       ""}]))
        (doall
         (for [[_ _ entity :as item-data] activities]
           (if (= :entity.type/value-creation (:entity/type entity))
             ^{:key (str "value-creation-elem-" (:db/id entity))}
             [value-creation-elem
              {:state            state
               :local-entities   local-entities
               :viewed-entity-id primary-entity-id
               :entity           entity
               :class-name       ""}]
             ^{:key (str "activity-elem-" (:db/id entity))}
             [activity-elem
              {:state            state
               :local-entities   local-entities
               :viewed-entity-id primary-entity-id
               :entity           entity
               :class-name       (when (= item-data last-data)
                                   :last-item)}])))
        [:div.activity-entry.footer
         (when (and (not show-all?)
                    (> item-count show-count))
           [:div.activity-entry-left.clickable
            {:on-click show-more!}
            [:span
             (loc/tr :show-more)]])]])]))




;; Activity timeline

(defn tab-elem
  [{:keys [state default-view view-kw kw icon fa-style on-click!]}]
  (let [current-view (or (get @state view-kw) default-view)]
    [:span
     {:class-name (fu/cs "tab" (and (= current-view kw) "active"))
      :on-click   (fn [e]
                    (when-not (and on-click!
                                   (on-click!))
                      (swap! state assoc view-kw kw)
                      (swap! state dissoc :show-count)
                      (dg/prevent-and-stop e)))}

     [:i {:class-name (fu/cs (or fa-style "fal") (name icon))}]
     [:span (loc/tr kw)]]))

(def default-event-view :activity-view/tab-activity)

(defn all-activity-types
  []
  (set/union
   #{:entity.type/note :entity.type/note-call :entity.type/note-meeting :entity.type/note-message
     :entity.type/email :entity.type/system-activity}
   (when (session/has-module-access? @session/account :module/value-creation-activity)
     #{:entity.type/value-creation})))

(defn reverse-relationships
  [local-entities entity-or-id]
  (let [eid (or (:db/id entity-or-id)
                entity-or-id)
        reverse-ids (->> @local-entities
                         vals
                         (filter (fn [{:keys [common/related-entities] :as _reverse-entity}]
                                   (contains? related-entities eid)))
                         (map :db/id))]
    (when (seq reverse-ids)
      (set reverse-ids))))

(defn reverse-relationships-value-creations
  [local-entities entity-or-id]
  (let [eid (or (:db/id entity-or-id)
                entity-or-id)
        reverse-ids (->> @local-entities
                         vals
                         (filter (fn [{:keys [value-creation/parent-entity]}]
                                   (= parent-entity eid)))
                         (map :db/id))]
    (when (seq reverse-ids)
      (set reverse-ids))))

(defn toggle-expanded
  [dropdown-state _primary-entity-id]
  [:ul {:class-name (fu/cs "create-dropdown-container pb-1")}
   (if (:expand-all? @dropdown-state)
     [button/square-secondary-open
      {:tooltip  (loc/tr :collapse-all)
       :on-click (fn [_]
                   (swap! dropdown-state dissoc :expand-all? :override-expand-entity))}
      [:i.fal.fa-compress-alt]]

     [button/square-secondary-open
      {:tooltip  (loc/tr :expand-all)
       :on-click (fn [_]
                   (swap! dropdown-state assoc :expand-all? true)
                   (swap! dropdown-state dissoc :override-expand-entity))}
      [:i.fal.fa-expand-alt]])])


(defn add-activity-dropdown
  [dropdown-state primary-entity-id]
  (let [open-modal (fn [entity-type]
                     (modals/open-modal! activity-entity-editor/modal-key
                                         {:new-entity-type  entity-type
                                          :viewed-entity-id primary-entity-id}))]
    [:ul {:class-name (fu/cs "create-dropdown-container pb-1")}
     [rbs/Dropdown
      {:id         "global-navbar-create-dropdown"
       :class-name (fu/cs "")
       :on-toggle  (fn [e]
                     (swap! dropdown-state assoc :dropdown-status/create (boolean e)))}
      [rbs/Dropdown-Toggle
       {:id         "global-navbar-create-dropdown"
        :class-name "p-0"
        :variant    "button"}
       [:i
        {:class-name (fu/cs "fal clickable fa-plus-circle")}]]

      [rbs/Dropdown-Menu

       [rbs/DropdownItem
        {:event-key  "collapse-all-activities"
         :class-name ""
         :on-select  #(open-modal :entity.type/note)}
        [:span
         (loc/tr :add-note)]]

       [rbs/DropdownItem
        {:event-key  "collapse-all-activities"
         :class-name ""
         :on-select  #(open-modal :entity.type/note-call)}
        [:span
         (loc/tr :log-call)]]

       [rbs/DropdownItem
        {:event-key  "collapse-all-activities"
         :class-name ""
         :on-select  #(open-modal :entity.type/note-meeting)}
        [:span
         (loc/tr :log-meeting)]]

       [rbs/DropdownItem
        {:event-key  "collapse-all-activities"
         :class-name ""
         :on-select  #(open-modal :entity.type/note-message)}
        [:span
         (loc/tr :log-message)]]]]]))

(defn view-component
  [args]
  (let [{:keys [entity-ids primary-entity-id]} args
        primary-entity-id (or primary-entity-id (first entity-ids))
        state (r/atom {:max-count 50})
        local-entities (r/cursor state [:entities])
        subscription-args (assoc args
                                 :state state
                                 :primary-entity-id primary-entity-id)]
    (r/create-class
     {:component-did-mount
      (fn [_]
        (swap! state assoc
               :subscribed-ids entity-ids
               :last-subscription (zap-time/now))
        (deref state)
        (events/open-history-subscription! subscription-args)
        (events/open-emails-subscription! subscription-args))
      :component-will-unmount
      (fn [_]
        (events/stop-history-subscription! state)
        (events/stop-emails-subscription! state)
        (events/stop-audit-subscription! state))
      :reagent-render
      (fn [{:keys [entity-ids primary-entity-id]}]
        (let [show-more! (fn []
                           (swap! state update :show-count (fnil + 5) 20)
                           (let [not-yet-shown (- (:max-count @state) (:show-count @state))]
                             (when (> 10 not-yet-shown)
                               (swap! state update :max-count (fnil * 50) 2)
                               (events/open-history-subscription! subscription-args)
                               (events/open-emails-subscription! subscription-args))))
              primary-entity-id (or primary-entity-id (first entity-ids))
              primary-entity (get @session/entities primary-entity-id)
              relationship-datas-by-entity-type
              (->> entity-ids
                   (mapcat
                    (fn [entity-id]
                      (concat (->> (concat @(r/track session/entity-reverse-relationships entity-id)
                                           @(r/track reverse-relationships local-entities entity-id)
                                           @(r/track reverse-relationships-value-creations
                                                     local-entities
                                                     entity-id))
                                   (map (fn [related-id]
                                          [related-id entity-id]))
                                   (doall))
                              (when-not (tenant-customizations/capman?)
                                (resolve-associated-people-email-ids-for-entity
                                 local-entities
                                 entity-id)))))
                   (keep (fn [[related-id eid]]
                           (let [related-entity (or (get @local-entities related-id)
                                                    (get @session/entities related-id))
                                 parent-entity (:value-creation/parent-entity related-entity)
                                 entity-type (:entity/type related-entity)]
                             (when (or (= eid parent-entity)
                                       (and
                                        (some (set (:common/related-entities related-entity))
                                              (set (conj entity-ids eid)))                          ;; not removed from reverse
                                        (some #{entity-type} (all-activity-types))
                                        (not (:common/disabled? related-entity))))
                               [(:entity/type related-entity) eid related-entity]))))
                   (group-by first))]
          [:div.entity-right
           (if-not (:timeline-loaded @state)
             [:div.entity-timeline-loading
              [:i.fal.fa-spinner.fa-pulse.fa-2x]
              [:span (loc/tr :updating-activity)]]
             [:div.activity-timeline
              (let [event-view (or (:activity-view @state) default-event-view)
                    activity-params {:state        state
                                     :default-view default-event-view
                                     :view-kw      :activity-view}]
                [:div
                 [:div.activity-selector.justify-content-between.align-items-center
                  [:div
                   [tab-elem
                    (merge activity-params
                           {:kw   :activity-view/tab-activity
                            :icon :fa-globe})]
                   (when (:entity.type/note relationship-datas-by-entity-type)
                     [tab-elem
                      (merge activity-params
                             {:kw   :activity-view/tab-notes
                              :icon :fa-edit})])
                   (when (:entity.type/email relationship-datas-by-entity-type)
                     [tab-elem
                      (merge activity-params
                             {:kw   :activity-view/tab-emails
                              :icon :fa-envelope})])
                   (when (:entity.type/note-call relationship-datas-by-entity-type)
                     [tab-elem
                      (merge activity-params
                             {:kw   :activity-view/tab-calls
                              :icon :fa-phone})])
                   (when (:entity.type/note-meeting relationship-datas-by-entity-type)
                     [tab-elem
                      (merge activity-params
                             {:kw   :activity-view/tab-meetings
                              :icon :fa-users})])
                   (when (:entity.type/note-message relationship-datas-by-entity-type)
                     [tab-elem
                      (merge activity-params
                             {:kw   :activity-view/tab-messages
                              :icon :fa-comments})])

                   (when (:entity.type/value-creation relationship-datas-by-entity-type)
                     [tab-elem
                      (merge activity-params
                             {:kw   :activity-view/tab-value-creations
                              :icon :fa-dollar-sign})])

                   (when-not (session/is-guest?)
                     [tab-elem
                      (merge activity-params
                             {:kw   :activity-view/tab-history
                              :icon :fa-history})])]

                  [:div.d-flex
                   {:style {:gap "10px"}}
                   [toggle-expanded state primary-entity-id]
                   #_[add-activity-dropdown state primary-entity-id]]]

                 (case event-view
                   :activity-view/tab-activity [entity-activity-view primary-entity-id state
                                                show-more! local-entities
                                                relationship-datas-by-entity-type
                                                (all-activity-types)]
                   :activity-view/tab-emails [entity-activity-view primary-entity-id state
                                              show-more! local-entities
                                              relationship-datas-by-entity-type
                                              #{:entity.type/email}]
                   :activity-view/tab-notes [entity-activity-view primary-entity-id state show-more!
                                             local-entities relationship-datas-by-entity-type
                                             #{:entity.type/note}]
                   :activity-view/tab-calls [entity-activity-view primary-entity-id state show-more!
                                             local-entities relationship-datas-by-entity-type
                                             #{:entity.type/note-call}]
                   :activity-view/tab-meetings [entity-activity-view primary-entity-id state
                                                show-more! local-entities
                                                relationship-datas-by-entity-type
                                                #{:entity.type/note-meeting}]
                   :activity-view/tab-messages [entity-activity-view primary-entity-id state
                                                show-more! local-entities
                                                relationship-datas-by-entity-type
                                                #{:entity.type/note-message}]
                   :activity-view/tab-value-creations [entity-activity-view primary-entity-id state
                                                       show-more! local-entities
                                                       relationship-datas-by-entity-type
                                                       #{:entity.type/value-creation}]
                   :activity-view/tab-history [history-activity/view primary-entity-id state
                                               local-entities (:entity/type primary-entity)]
                   [:div (loc/tr :activity-view/not-implemented)])])])]))})))


(defn view
  [{:keys [entity-ids] :as args}]
  ^{:key (str entity-ids)}
  [view-component args])
